'use client'

import clsx from 'clsx'
import { useTranslation } from 'react-i18next'
import { useProductMetadata } from '@/components/LayoutWithMenu/productMetadataHooks'
import { Pillow } from '@/components/Pillow/Pillow'
import { type ProductFragment } from '@/services/graphql/generated'
import { Text } from 'ui/src/components/Text/Text'
import { yStack } from 'ui/src/patterns/stack'
import { BUNDLE_DISCOUNT_PERCENTAGE } from '../../bundleDiscount.constants'
import { card, items, pillowImage, threeInsurances, twoInsurances } from './BundleDiscountOverview.css'

export const PILLOW_PRODUCT_IDS = new Set(['Product:SE_APARTMENT_BRF', 'Product:SE_CAR', 'Product:SE_PET_DOG'])

export function BundleDiscountOverview() {
  const { t } = useTranslation()
  const productMetadata = useProductMetadata()
  const pillows = productMetadata?.filter((item) => PILLOW_PRODUCT_IDS.has(item.id)).map((item) => item.pillowImage)

  return (
    <div className={yStack({ gap: 'xs' })}>
      <div className={items}>
        <BundleLevel
          title={t('BUNDLE_DISCOUNT_PROGRESS_SEGMENT_TITLE_ONE_INSURANCE')}
          subtitle={t('BUNDLE_DISCOUNT_PROGRESS_SEGMENT_SUBTITLE_NO_DISCOUNT')}
          pillows={pillows?.slice(0, 1)}
        />

        <BundleLevel
          className={twoInsurances}
          title={t('BUNDLE_DISCOUNT_PROGRESS_SEGMENT_TITLE_TWO_INSURANCES')}
          subtitle={t('BUNDLE_DISCOUNT_PROGRESS_SEGMENT_SUBTITLE_APPLIED_DISCOUNT', {
            percentage: BUNDLE_DISCOUNT_PERCENTAGE,
          })}
          pillows={pillows?.slice(0, 2)}
        />

        <BundleLevel
          className={threeInsurances}
          title={t('BUNDLE_DISCOUNT_PROGRESS_SEGMENT_TITLE_THREE_OR_MORE')}
          subtitle={t('BUNDLE_DISCOUNT_PROGRESS_SEGMENT_SUBTITLE_APPLIED_DISCOUNT', {
            percentage: BUNDLE_DISCOUNT_PERCENTAGE,
          })}
          pillows={pillows?.slice(0, 3)}
        />
      </div>
    </div>
  )
}

type BundleLevelProps = {
  className?: string
  title: string
  subtitle: string
  pillows?: Array<ProductFragment['pillowImage']>
}

function BundleLevel({ title, subtitle, pillows, className }: BundleLevelProps) {
  return (
    <div className={clsx(yStack({ gap: 'xs', alignItems: 'center' }), className)}>
      <div className={card}>
        {pillows?.map((pillow, index) => (
          <Pillow key={index} className={pillowImage} src={pillow.src} size="small" alt={pillow.alt} />
        ))}
      </div>
      <div className={yStack({ alignItems: 'center' })}>
        <Text as="span" size="xs" color="textPrimary">
          {title}
        </Text>
        <Text as="span" size="xs" color="textSecondary">
          {subtitle}
        </Text>
      </div>
    </div>
  )
}
